import { ComponentProps, useContext, useState } from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { LegacyPrimaryButton as PrimaryButton } from 'components/Button/LegacyPrimaryButton';
import { FieldLabel } from 'components/Form/Field/FieldLabel';
import { BodyLarge } from 'components/Text/BodyLarge';
import { RichTextHeadline } from 'components/Text/RichTextHeadline';
import { TabContext } from 'containers/TabContext';
import { CmsApiFormBuilderBlockData } from 'modules/cms/api/types/blocks/CmsApiFormBuilderBlock';
import { colors } from 'theme';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { imageUrl } from 'utils/filestack';
import { getStaticImageSrc } from 'utils/getImageSrc';

import { CmsFormBuilderBlockContainer } from './CmsFormBuilderBlockContainer';
import { CmsFormBuilderBlockField } from './CmsFormBuilderBlockField';
import { getCmsFormBuilderId } from './getCmsFormBuilderId';

const StyledLabel = styled(FieldLabel)`
  color: white;
  margin-bottom: 6px;
`;

const SubmitButton = styled(
  ({ ...props }: ComponentProps<typeof PrimaryButton>) => (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <PrimaryButton {...props} />
  ),
)`
  padding-left: 40px;
  padding-right: 40px;
  max-width: 440px;
  box-shadow: 0 0 0 1px white inset;
  background: none;
  color: white;
  &:focus,
  &:hover {
    box-shadow: 0 0 0 1px ${colors.brandGreen} inset;
    background: ${colors.brandGreen};
  }
`;

type Props = {
  data: CmsApiFormBuilderBlockData;
};

export function CmsFormBuilderBlock({
  data: { headline, submitButtonText, bgImage, action, newTab, fields, method },
}: Props) {
  const isTabbing = useContext(TabContext);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = () => {
    setSubmitted(true);
    trackEvent('CMS Form Submitted', {
      action,
    });
  };

  return (
    <div>
      <Box
        p="80px 20px"
        width="auto !important"
        borderRadius={[0, 0, 0, 24]}
        background={`url("${
          bgImage
            ? imageUrl({
                data: bgImage,
                width: 2096,
                height: undefined,
                fit: 'max',
                insecureSVG: false,
              })
            : getStaticImageSrc('blue-loops.svg')
        }") center/cover`}
      >
        {headline && (
          <Box maxWidth="960px" m="0 auto">
            <RichTextHeadline color="white" textAlignCenter>
              {headline}
            </RichTextHeadline>
          </Box>
        )}

        <Box position="relative">
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            display={submitted ? undefined : 'none'}
            width={1}
          >
            <BodyLarge color="white" textAlign="center">
              {getText('Thank you!')}
            </BodyLarge>
          </Box>

          <Box visibility={submitted && 'hidden'}>
            <CmsFormBuilderBlockContainer $isTabbing={isTabbing}>
              <form
                action={action}
                target={newTab ? '_blank' : undefined}
                onSubmit={onSubmit}
                method={method}
              >
                <Box maxWidth="440px" m="40px auto 0">
                  {fields.map((field) => (
                    <Box
                      my={field.type === 'CHECKBOX' ? 24 : 16}
                      key={field.name}
                    >
                      {!['CHECKBOX', 'HIDDEN'].includes(field.type) && (
                        <StyledLabel htmlFor={getCmsFormBuilderId(field.name)}>
                          {/* TODO fix next line next time working on file */}
                          {/* @ts-expect-error Error appeared when improving types */}
                          {field.label}
                        </StyledLabel>
                      )}
                      <CmsFormBuilderBlockField field={field} action={action} />
                    </Box>
                  ))}
                  <Box classes={['flex', 'justify-center']} mt="40px">
                    <SubmitButton $size="large" type="submit">
                      {submitButtonText}
                    </SubmitButton>
                  </Box>
                </Box>
                <div
                  style={{
                    position: 'absolute',
                    left: -9999,
                    top: -9999,
                  }}
                >
                  <label htmlFor="pardot_extra_field">
                    {getText(`Comments (No Use)`)}
                  </label>
                  <input
                    type="text"
                    id="pardot_extra_field"
                    name="pardot_extra_field"
                  />
                </div>
              </form>
            </CmsFormBuilderBlockContainer>
          </Box>
        </Box>
      </Box>
    </div>
  );
}
